/**
 * Listings Data Module
 * Exports all listing data from JSON files and provides helper functions
 */

import farmsData from './farms.json';
import marketsData from './markets.json';
import restaurantsData from './restaurants.json';
import csaData from './csa.json';
import upickData from './upick.json';
import specialtyData from './specialty.json';
import scrapedGeneratedData from './scraped-generated.json';

const normalizeImages = (images, fallback) => {
  if (Array.isArray(images)) return images.filter(Boolean);
  if (!images || typeof images !== 'object') return fallback ? [fallback] : [];

  const normalized = [];
  if (images.hero) normalized.push(images.hero);
  if (images.heroImage) normalized.push(images.heroImage);
  if (Array.isArray(images.gallery)) normalized.push(...images.gallery);
  return normalized.filter(Boolean);
};

const normalizeListing = (listing) => {
  const address = listing?.address && typeof listing.address === 'object' ? listing.address : {};

  const street =
    address.street ||
    address.address ||
    address.street1 ||
    '';

  const city = address.city || '';
  const state = address.state || '';
  const zip = address.zip || address.zipCode || '';

  const fallbackImage = listing?.logo || '/images/placeholders/produce-fallback.svg';

  return {
    ...listing,
    id: listing?.id,
    slug: listing?.slug || listing?.id,
    type: listing?.type,
    subtype: listing?.subtype,

    description: listing?.description || '',
    shortDescription: listing?.shortDescription || listing?.description || '',

    address: street,
    city,
    state,
    zip,

    coordinates: listing?.coordinates || null,

    phone: listing?.phone || '',
    email: listing?.email || '',
    website: listing?.website || '',

    hours: listing?.hours || null,

    products: Array.isArray(listing?.products) ? listing.products : [],
    tags: Array.isArray(listing?.tags) ? listing.tags : [],
    amenities: Array.isArray(listing?.amenities) ? listing.amenities : [],

    logo: listing?.logo || '',
    images: normalizeImages(listing?.images, fallbackImage),

    rating: typeof listing?.rating === 'number' ? listing.rating : 0,
    reviewCount: typeof listing?.reviewCount === 'number' ? listing.reviewCount : 0,

    featured: Boolean(listing?.featured),

    seasonalAvailability: listing?.seasonalAvailability || null,
    seo: listing?.seo || null,
  };
};

// Export individual category data (normalized to match app expectations)
export const farms = farmsData.map(normalizeListing);
export const markets = marketsData.map(normalizeListing);
export const restaurants = restaurantsData.map(normalizeListing);
export const csa = csaData.map(normalizeListing);
export const upick = upickData.map(normalizeListing);
export const specialty = specialtyData.map(normalizeListing);
export const scraped = scrapedGeneratedData.map(normalizeListing);

// Combined listings array
export const listings = [...farms, ...markets, ...restaurants, ...csa, ...upick, ...specialty, ...scraped];

// Total count
export const listingsCount = listings.length;

/**
 * Get listing by ID
 * @param {string} id - The listing ID
 * @returns {Object|undefined} The listing object or undefined
 */
export const getListingById = (id) => {
  return listings.find(listing => listing.id === id);
};

/**
 * Get listing by slug
 * @param {string} slug - The listing slug
 * @returns {Object|undefined} The listing object or undefined
 */
export const getListingBySlug = (slug) => {
  return listings.find(listing => listing.slug === slug);
};

/**
 * Get listings by type
 * @param {string} type - The listing type (farm, market, restaurant, csa, upick, specialty)
 * @returns {Array} Array of listings matching the type
 */
export const getListingsByType = (type) => {
  return listings.filter(listing => listing.type === type);
};

/**
 * Get listings by subtype
 * @param {string} subtype - The listing subtype
 * @returns {Array} Array of listings matching the subtype
 */
export const getListingsBySubtype = (subtype) => {
  return listings.filter(listing => listing.subtype === subtype);
};

/**
 * Get featured listings
 * @param {number} limit - Optional limit on number of results
 * @returns {Array} Array of featured listings
 */
export const getFeaturedListings = (limit = null) => {
  const featured = listings.filter(listing => listing.featured);
  return limit ? featured.slice(0, limit) : featured;
};

/**
 * Get listings by city
 * @param {string} city - The city name
 * @returns {Array} Array of listings in the specified city
 */
export const getListingsByCity = (city) => {
  return listings.filter(listing =>
    listing.city.toLowerCase() === city.toLowerCase()
  );
};

/**
 * Get listings by tag
 * @param {string} tag - The tag to filter by
 * @returns {Array} Array of listings with the specified tag
 */
export const getListingsByTag = (tag) => {
  return listings.filter(listing =>
    listing.tags && listing.tags.includes(tag)
  );
};

/**
 * Get listings by multiple tags (AND logic)
 * @param {Array} tags - Array of tags to filter by
 * @returns {Array} Array of listings with all specified tags
 */
export const getListingsByTags = (tags) => {
  return listings.filter(listing =>
    listing.tags && tags.every(tag => listing.tags.includes(tag))
  );
};

/**
 * Search listings by query string
 * @param {string} query - Search query
 * @returns {Array} Array of matching listings
 */
export const searchListings = (query) => {
  const lowerQuery = query.toLowerCase();
  return listings.filter(listing =>
    listing.name.toLowerCase().includes(lowerQuery) ||
    listing.description.toLowerCase().includes(lowerQuery) ||
    listing.shortDescription.toLowerCase().includes(lowerQuery) ||
    listing.city.toLowerCase().includes(lowerQuery) ||
    (listing.tags && listing.tags.some(tag => tag.toLowerCase().includes(lowerQuery))) ||
    (listing.products && listing.products.some(product => product.toLowerCase().includes(lowerQuery)))
  );
};

/**
 * Get listings within a radius of coordinates
 * @param {number} centerLat - Center latitude
 * @param {number} centerLng - Center longitude
 * @param {number} radiusMiles - Radius in miles
 * @returns {Array} Array of listings within the radius, sorted by distance
 */
export const getListingsWithinRadius = (centerLat, centerLng, radiusMiles) => {
  const toRad = (deg) => deg * (Math.PI / 180);

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959; // Earth radius in miles
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  return listings
    .map(listing => ({
      ...listing,
      distance: calculateDistance(
        centerLat,
        centerLng,
        listing.coordinates.lat,
        listing.coordinates.lng
      )
    }))
    .filter(listing => listing.distance <= radiusMiles)
    .sort((a, b) => a.distance - b.distance);
};

/**
 * Get listings by product
 * @param {string} product - Product to search for
 * @returns {Array} Array of listings offering the product
 */
export const getListingsByProduct = (product) => {
  const lowerProduct = product.toLowerCase();
  return listings.filter(listing =>
    listing.products && listing.products.some(p =>
      p.toLowerCase().includes(lowerProduct)
    )
  );
};

/**
 * Get listings by amenity
 * @param {string} amenity - Amenity to filter by
 * @returns {Array} Array of listings with the specified amenity
 */
export const getListingsByAmenity = (amenity) => {
  return listings.filter(listing =>
    listing.amenities && listing.amenities.includes(amenity)
  );
};

/**
 * Get all unique cities from listings
 * @returns {Array} Sorted array of unique city names
 */
export const getAllCities = () => {
  const cities = [...new Set(listings.map(listing => listing.city).filter(Boolean))];
  return cities.sort();
};

/**
 * Get all unique tags from listings
 * @returns {Array} Sorted array of unique tags
 */
export const getAllTags = () => {
  const tags = listings.reduce((acc, listing) => {
    if (listing.tags) {
      listing.tags.forEach(tag => acc.add(tag));
    }
    return acc;
  }, new Set());
  return [...tags].sort();
};

/**
 * Get listings count by type
 * @returns {Object} Object with type as key and count as value
 */
export const getListingsCountByType = () => {
  return listings.reduce((acc, listing) => {
    acc[listing.type] = (acc[listing.type] || 0) + 1;
    return acc;
  }, {});
};

/**
 * Get listings with seasonal availability for current season
 * @param {string} season - Season name (spring, summer, fall, winter)
 * @returns {Array} Array of listings with availability in the specified season
 */
export const getListingsBySeason = (season) => {
  const lowerSeason = season.toLowerCase();
  return listings.filter(listing => {
    if (!listing.seasonalAvailability) return true;
    const seasonData = listing.seasonalAvailability[lowerSeason];
    if (!seasonData) return false;
    // Check if closed/limited
    if (Array.isArray(seasonData)) {
      return !seasonData.some(item =>
        item.toLowerCase().includes('closed') ||
        item.toLowerCase().includes('limited')
      );
    }
    return true;
  });
};

/**
 * Get related listings
 * @param {string} listingId - The source listing ID
 * @param {number} limit - Maximum number of related listings to return
 * @returns {Array} Array of related listings
 */
export const getRelatedListings = (listingId, limit = 4) => {
  const sourceListing = getListingById(listingId);
  if (!sourceListing) return [];

  // Score each listing based on similarity
  const scored = listings
    .filter(listing => listing.id !== listingId)
    .map(listing => {
      let score = 0;

      // Same type
      if (listing.type === sourceListing.type) score += 5;

      // Same city
      if (listing.city === sourceListing.city) score += 3;

      // Shared tags
      if (listing.tags && sourceListing.tags) {
        const sharedTags = listing.tags.filter(tag =>
          sourceListing.tags.includes(tag)
        );
        score += sharedTags.length * 2;
      }

      // Shared products
      if (listing.products && sourceListing.products) {
        const sharedProducts = listing.products.filter(product =>
          sourceListing.products.includes(product)
        );
        score += sharedProducts.length;
      }

      return { ...listing, score };
    })
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit);
};

// Default export
export default listings;
